<template>
  <v-menu v-model="activeMenu" :close-on-content-click="false" class="time-picker">
    <template v-slot:activator="{ on, attrs }">
      <div class="time-picker__row">
        <div class="time-picker__row__input-container">
          <slot :on="on" :attrs="attrs" name="activator" />

          <v-menu v-model="activeClockMenu" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="time"
                v-on="on"
                v-bind="attrs"
                @click:clear="setTimeToNull"
                :disabled="disabled || !time"
                :rbt-target="toCamelCase(`timePicker ${rbtTarget}`)"
                :rules="[inputRule]"
                class="time-picker__input--time-format"
                append-icon="mdi-clock-outline"
                readonly
                hide-details
                clearable
              >
                <template v-slot:append>
                  <v-icon v-if="!disabled && time" v-on="on">mdi-clock-outline</v-icon>
                </template>
              </v-text-field>
            </template>

            <v-time-picker
              v-model="time"
              @click:minute="closeTimePicker"
              :disabled="disabled"
              :allowed-hours="allowedHours"
              :allowed-minutes="allowedMinutes"
              color="primary"
              format="24hr"
              landscape
            />
          </v-menu>
        </div>

        <span v-if="validationLabel" class="time-picker__validation">
          <v-icon left color="error">mdi-information-outline</v-icon>
          <span>{{ validationLabel }}</span>
        </span>
      </div>
    </template>

    <v-date-picker
      v-model="datePickerDate"
      @change="onDateChange"
      :allowed-dates="allowedDates"
      :disabled="disabled"
      color="primary"
      landscape
    />
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick, PropType } from "@vue/composition-api"
import { TimeFormat } from "@/utils/input-rules"
import { TIME_BETWEEN_START_AND_END } from "../index"
import { createTranslationModule, translate } from "@/plugins/i18n"
import Utils from "@/utils/utils"

export default defineComponent({
  name: "TimePicker",
  props: {
    value: {
      type: null as unknown as PropType<number | null>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: [String, Number],
      required: false
    },
    maxDate: {
      type: [String, Number],
      required: false
    },
    showValidation: {
      type: [Boolean, String],
      default: false
    },
    rbtTarget: {
      type: String,
      required: true
    }
  },
  setup(props, ctx) {
    const t = createTranslationModule("AutoShutdown.scheduleSession")
    const date = Utils.vModel(props, ctx.emit)
    const activeMenu = ref(false)
    const activeClockMenu = ref(false)

    const datePickerDate = computed({
      get() {
        if (date.value) {
          const timezone = Utils.minutes(new Date().getTimezoneOffset())
          return new Date(date.value - timezone).toISOString().slice(0, 10)
        }

        return ""
      },
      set(newDate: string) {
        const now = new Date()
        const theDate = new Date(newDate)
        const safeTimeGap = 5
        const minRangeGapInMinutes = TIME_BETWEEN_START_AND_END / Utils.minutes(1) + safeTimeGap

        theDate.setDate(theDate.getDate())
        theDate.setHours(now.getHours())
        theDate.setMinutes(now.getMinutes() + minRangeGapInMinutes)
        theDate.setSeconds(now.getSeconds())

        date.value = theDate.getTime()
      }
    })

    const time = computed({
      get() {
        if (date.value) {
          const formattedDate = Utils.dateFormatWithTime(date.value)
          return formattedDate.slice(formattedDate.indexOf(",") + 2)
        }

        return ""
      },
      set(hour: string) {
        const timeFormatRule = TimeFormat[0]

        if (timeFormatRule(hour) === true) {
          const newDate = new Date(date.value as number)
          const [hh, mm] = hour.split(":")

          newDate.setHours(parseInt(hh))
          newDate.setMinutes(parseInt(mm))

          date.value = newDate.getTime()
        } else {
          date.value = null
        }
      }
    })

    const validationLabel = computed(() => {
      if (props.showValidation && typeof props.showValidation === "string") {
        return translate(props.showValidation)
      } else {
        return false
      }
    })

    const toCamelCase = (word: string) => {
      return Utils.toCamel(word)
    }

    const inputRule = () => {
      if (date.value) {
        if (props.maxDate) {
          return date.value < new Date(props.maxDate).getTime() - TIME_BETWEEN_START_AND_END
        } else if (props.minDate) {
          return date.value > new Date(props.minDate).getTime() + TIME_BETWEEN_START_AND_END
        }
      }

      return true
    }

    const allowedHours = (hour: number) => {
      if (date.value) {
        const newDateInMsWithAlteredTime = new Date(date.value).setHours(hour)

        if (props.minDate) {
          return newDateInMsWithAlteredTime >= new Date(props.minDate).getTime() + TIME_BETWEEN_START_AND_END
        } else if (props.maxDate) {
          return newDateInMsWithAlteredTime <= new Date(props.maxDate).getTime() - TIME_BETWEEN_START_AND_END
        }

        return newDateInMsWithAlteredTime >= Date.now()
      }

      return false
    }

    const allowedMinutes = (min: number) => {
      if (date.value) {
        const newDateInMsWithAlteredTime = new Date(date.value).setMinutes(min)

        if (props.minDate) {
          return newDateInMsWithAlteredTime > new Date(props.minDate).getTime() + TIME_BETWEEN_START_AND_END
        } else if (props.maxDate) {
          return newDateInMsWithAlteredTime < new Date(props.maxDate).getTime() - TIME_BETWEEN_START_AND_END
        }

        return newDateInMsWithAlteredTime >= Date.now() + TIME_BETWEEN_START_AND_END
      }

      return false
    }

    const allowedDates = (fromCalendarDate: string) => {
      const calendarDate = new Date(fromCalendarDate)

      calendarDate.setDate(calendarDate.getDate() + 1)

      if (props.minDate) {
        return calendarDate.getTime() > new Date(props.minDate).getTime() + TIME_BETWEEN_START_AND_END
      } else if (props.maxDate) {
        return calendarDate.getTime() < new Date(props.maxDate).getTime() - TIME_BETWEEN_START_AND_END
      }

      return calendarDate.getTime() >= Date.now() + TIME_BETWEEN_START_AND_END
    }

    const setTimeToNull = () => {
      date.value = null
    }

    const onDateChange = () => {
      nextTick(() => {
        if (datePickerDate.value) {
          activeMenu.value = false
          activeClockMenu.value = true

          ctx.emit("change")
        }
      })
    }

    const closeTimePicker = () => {
      activeClockMenu.value = false
      ctx.emit("change")
    }

    return {
      t,
      validationLabel,
      inputRule,
      toCamelCase,
      closeTimePicker,
      onDateChange,
      setTimeToNull,
      allowedHours,
      allowedMinutes,
      allowedDates,
      activeClockMenu,
      time,
      datePickerDate,
      date,
      activeMenu
    }
  }
})
</script>
